import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Video, MapPin, ArrowRight, Loader2 } from "lucide-react";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactSection = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please share your name, email, and a little about what's going on.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Received. The Wicks will reach out to you personally.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  const inputClass =
    "w-full bg-transparent border-b border-[#E6DFD5] focus:border-[#8A5A44] outline-none py-3 text-[#2C2A28] placeholder:text-[#5C5854]/50 text-sm transition-colors duration-300";

  return (
    <section id="contact" className="py-24 lg:py-32" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-12 gap-12">
        <div className="col-span-12 lg:col-span-5">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-[#8A5A44] mb-4">07 — Connect</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl font-medium tracking-tight text-[#2C2A28]">
              Ready for that first <em className="italic text-[#8A5A44]">conversation?</em>
            </h2>
            <p className="text-[#5C5854] leading-relaxed font-light mt-6 max-w-md">
              Tell us a little about where you are. No situation is too far gone, and nothing you
              share leaves the room.
            </p>
            <div className="mt-10 space-y-5">
              <div className="flex items-center gap-4" data-testid="contact-email">
                <Mail size={18} strokeWidth={1.5} className="text-[#8A5A44]" />
                <a
                  href="mailto:wicksmarriageministries@gmail.com"
                  className="text-sm text-[#2C2A28] hover:text-[#8A5A44] transition-colors duration-300"
                >
                  wicksmarriageministries@gmail.com
                </a>
              </div>
              <div className="flex items-center gap-4" data-testid="contact-location">
                <MapPin size={18} strokeWidth={1.5} className="text-[#8A5A44]" />
                <span className="text-sm text-[#5C5854]">Greensboro, North Carolina</span>
              </div>
              <div className="flex items-center gap-4" data-testid="contact-video">
                <Video size={18} strokeWidth={1.5} className="text-[#8A5A44]" />
                <span className="text-sm text-[#5C5854]">Video coaching available across the U.S.</span>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="col-span-12 lg:col-span-6 lg:col-start-7">
          <Reveal delay={0.15}>
            <form
              onSubmit={submit}
              className="bg-[#F3EFEA] rounded-3xl p-10 lg:p-12 border border-black/5 space-y-7"
              data-testid="contact-form"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
                <input
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Your name *"
                  className={inputClass}
                  data-testid="contact-input-name"
                />
                <input
                  value={form.email}
                  onChange={set("email")}
                  type="email"
                  placeholder="Email *"
                  className={inputClass}
                  data-testid="contact-input-email"
                />
              </div>
              <input
                value={form.phone}
                onChange={set("phone")}
                placeholder="Phone (optional)"
                className={inputClass}
                data-testid="contact-input-phone"
              />
              <textarea
                value={form.message}
                onChange={set("message")}
                rows={4}
                placeholder="What's going on in your relationship? *"
                className={`${inputClass} resize-none`}
                data-testid="contact-input-message"
              />
              <button
                type="submit"
                disabled={sending}
                className="group rounded-full bg-[#8A5A44] hover:bg-[#734A38] disabled:opacity-60 text-[#FAF9F6] px-8 py-4 text-sm flex items-center gap-2 transition-colors duration-300"
                data-testid="contact-submit-button"
              >
                {sending ? <Loader2 size={16} className="animate-spin" /> : null}
                {sending ? "Sending…" : "Send it to the Wicks"}
                {!sending && (
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                )}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
